"use client";

import { Box, Typography } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import SectionContainer from "@/components/marketing/SectionContainer";

/**
 * InteractiveMap - Google Maps embed with click-to-interact overlay.
 * Prevents scroll hijacking while allowing users to interact after clicking.
 * Re-enables barrier when user clicks outside the map.
 */
export default function InteractiveMap() {
  const [isActive, setIsActive] = useState(false);
  const mapContainerRef = useRef(null);

  // Re-enable barrier when clicking outside the map
  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event) => {
      if (mapContainerRef.current && !mapContainerRef.current.contains(event.target)) {
        setIsActive(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isActive]);

  return (
    <SectionContainer py={{ xs: 8, md: 12 }} backgroundColor="background.section">
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "2rem", md: "2.5rem" },
            color: "text.primary",
            mb: 2,
            fontWeight: 600,
          }}
        >
          Serving El Paso Families
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          We&apos;re looking for a home in 79935, 79936, or 79938 zip codes. 
          Our location will ensure convenient access for families in El Paso.
        </Typography>
      </Box>

      {/* Map Container with Overlay */}
      <Box
        ref={mapContainerRef}
        sx={{
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          height: { xs: "300px", md: "400px" },
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 20px rgba(60, 86, 59, 0.08)",
        }}
      >
        {/* Invisible Click Barrier - blocks scroll events, enables map on click */}
        {!isActive && (
          <Box
            onClick={() => setIsActive(true)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              cursor: "default", // No special cursor to avoid drawing attention
            }}
          />
        )}

        {/* The Map Iframe - Exact same settings as original */}
        <iframe
          src="https://www.google.com/maps/d/u/0/embed?mid=17E3yAlyTNcmiQnN7KXE3qqFsPSqBV8U&ehbc=2E312F&t=1"
          width="100%"
          height="120%"
          style={{ 
            border: 0, 
            marginTop: "-70px",
            pointerEvents: isActive ? "auto" : "none" 
          }}
          title="East El Paso Target Area"
        />
      </Box>
    </SectionContainer>
  );
}
